class ExtMainProduct {
  constructor() {
    this.form = document.querySelector('.product-form');
    if (!this.form) return;

    // Variants data (from <script type="application/json" data-product-variants>...</script>)
    this.variants = this.readVariants();
    if (!this.variants.length) return;

    // Only the variant option fieldsets (keeps your original sold-out logic behavior correct)
    this.optionContainers = this.form.querySelectorAll('fieldset.product-option');

    // Hidden variant id input
    this.variantInput = this.form.querySelector('#product-variant-id');

    // Add to cart button + label
    this.addToCartBtn = this.form.querySelector('.add-to-cart');
    this.addToCartLabel = this.addToCartBtn?.querySelector('.button-label') || null;

    // Always use a stable default label (prevents capturing "Sold out" as the default)
    this.defaultAtcText = 'Add to cart';

    // Quantity controls
    this.qtyInput = this.form.querySelector('[data-qty-input]');
    this.qtyMinus = this.form.querySelector('[data-qty-minus]');
    this.qtyPlus = this.form.querySelector('[data-qty-plus]');

    // Price element
    this.priceEl = document.querySelector('.pdp-price');

    this.currentVariant = null;

    this.init();
  }

  init() {
    this.bindVariantEvents();
    this.bindQtyEvents();

    // Initial resolve (works for products with no options too)
    this.resolveCurrentVariant();
    this.updateSoldOutStates(); // ✅ keeps your original radio sold-out logic (variant.available)
    this.syncVariantId();

    // Clamp qty + update UI
    this.applyQtyLimits();
    this.updateDisplayedPrice();
    this.updateATCUI();
  }

  /* ===============================
    DATA
  =============================== */
  readVariants() {
    // Prefer inside form, but fallback just in case
    const jsonEl =
      this.form.querySelector('[data-product-variants]') ||
      document.querySelector('[data-product-variants]');

    if (!jsonEl) return [];

    try {
      const parsed = JSON.parse(jsonEl.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      console.warn('[ExtMainProduct] Invalid variants JSON', e);
      return [];
    }
  }

  /* ===============================
    ORIGINAL SOLD-OUT LOGIC (kept)
    - uses variant.available exactly like your original script
  =============================== */
  getSelectedOptions() {
    const selected = [];

    // Single-variant product (no option radios)
    if (!this.optionContainers.length) return selected;

    this.optionContainers.forEach((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      selected.push(checked ? checked.value : null);
    });

    return selected;
  }

  findMatchingVariant(optionsArray) {
    // If there are no options: match by hidden input value, else fallback to first available
    if (!optionsArray.length) {
      const idFromInput = Number(this.variantInput?.value);
      if (Number.isFinite(idFromInput) && idFromInput) {
        const byId = this.variants.find((v) => Number(v.id) === idFromInput);
        if (byId) return byId;
      }
      return this.variants.find((v) => v.available) || this.variants[0] || null;
    }

    return (
      this.variants.find((variant) => {
        if (!variant.options) return false;
        return variant.options.every((opt, i) => opt === optionsArray[i]);
      }) || null
    );
  }

  updateSoldOutStates() {
    // Only relevant if there are option radios
    if (!this.optionContainers.length) return;

    const selectedOptions = this.getSelectedOptions();

    this.optionContainers.forEach((fieldset, optionIndex) => {
      const radios = fieldset.querySelectorAll('input[type="radio"]');

      radios.forEach((radio) => {
        const hypotheticalOptions = [...selectedOptions];
        hypotheticalOptions[optionIndex] = radio.value;

        const matchingVariant = this.findMatchingVariant(hypotheticalOptions);
        const isAvailable = matchingVariant && matchingVariant.available;

        if (!isAvailable) radio.classList.add('sold-out');
        else radio.classList.remove('sold-out');
      });
    });
  }

  /* ===============================
    ADD TO CART UI
  =============================== */
  updateATCUI() {
    if (!this.addToCartBtn) return;

    const isAvailable = !!(this.currentVariant && this.currentVariant.available);

    // Class + disabled
    this.addToCartBtn.classList.toggle('sold-out', !isAvailable);
    this.addToCartBtn.disabled = !isAvailable;

    // Text
    if (this.addToCartLabel) {
      this.addToCartLabel.textContent = isAvailable ? this.defaultAtcText : 'Sold out';
    } else {
      // Fallback (avoid wiping markup if possible)
      this.addToCartBtn.textContent = isAvailable ? this.defaultAtcText : 'Sold out';
    }
  }

  /* ===============================
    VARIANT RESOLUTION + ID SYNC
  =============================== */
  resolveCurrentVariant() {
    const selectedOptions = this.getSelectedOptions();
    this.currentVariant = this.findMatchingVariant(selectedOptions);
  }

  syncVariantId() {
    if (this.currentVariant && this.variantInput) {
      this.variantInput.value = String(this.currentVariant.id);
    }
  }

  bindVariantEvents() {
    if (!this.optionContainers.length) return;

    this.optionContainers.forEach((fieldset) => {
      fieldset.addEventListener('change', () => {
        this.resolveCurrentVariant();
        this.updateSoldOutStates(); // ✅ keep original behavior
        this.syncVariantId();

        this.applyQtyLimits();
        this.updateDisplayedPrice();
        this.updateATCUI();
      });
    });
  }

  /* ===============================
    QUANTITY LIMITS
    - max is for qty control only
    - qty never becomes 0 (prevents price showing 0.00)
    - plus is disabled at max (max=1 works)
  =============================== */
  bindQtyEvents() {
    if (!this.qtyInput || !this.qtyMinus || !this.qtyPlus) return;

    this.qtyMinus.addEventListener('click', () => {
      this.setQty(Number(this.qtyInput.value) - 1);
    });

    this.qtyPlus.addEventListener('click', () => {
      this.setQty(Number(this.qtyInput.value) + 1);
    });

    this.qtyInput.addEventListener('input', () => {
      this.qtyInput.value = this.qtyInput.value.replace(/[^\d]/g, '');
    });

    this.qtyInput.addEventListener('blur', () => {
      this.setQty(this.qtyInput.value);
    });
  }

  getMinQty() {
    const min = Number(this.qtyInput?.getAttribute('min'));
    return Number.isFinite(min) && min > 0 ? min : 1;
  }

  getMaxQty() {
    if (!this.currentVariant) return null;

    const mgmt = this.currentVariant.inventory_management; // "shopify" or null
    const policy = this.currentVariant.inventory_policy;   // "deny" or "continue"
    const qty = Number(this.currentVariant.inventory_quantity);

    // Enforce max only if inventory is tracked and overselling is denied
    const enforce = !!mgmt && policy === 'deny';
    if (!enforce) return null;

    return Number.isFinite(qty) ? Math.max(0, qty) : 0;
  }

  sanitizeQty(value) {
    const min = this.getMinQty();
    const max = this.getMaxQty();

    let num = Number(value);
    if (!Number.isFinite(num)) num = min;

    num = Math.floor(num);
    num = Math.max(min, num);

    // If max is 0 (sold out), keep qty at min (1) so price still displays correctly.
    if (max !== null) {
      if (max < min) return min;
      num = Math.min(max, num);
    }

    return num;
  }

  setQty(value) {
    if (!this.qtyInput) return;

    this.qtyInput.value = String(this.sanitizeQty(value));
    this.applyQtyLimits();
    this.updateDisplayedPrice();
  }

  applyQtyLimits() {
    if (!this.qtyInput || !this.qtyMinus || !this.qtyPlus) return;

    const min = this.getMinQty();
    const max = this.getMaxQty();
    const current = this.sanitizeQty(this.qtyInput.value);

    this.qtyInput.value = String(current);

    // Set/remove max attribute only when meaningful
    if (max !== null && max >= min) this.qtyInput.setAttribute('max', String(max));
    else this.qtyInput.removeAttribute('max');

    this.qtyMinus.disabled = current <= min;

    // Disable plus at max OR fully disable when max < min (ex: max=0)
    if (max !== null) {
      this.qtyPlus.disabled = (max < min) ? true : current >= max;
    } else {
      this.qtyPlus.disabled = false;
    }
  }

  /* ===============================
    PRICE x QTY
    - uses Shopify.formatMoney + shop.money_format
  =============================== */
  formatMoney(cents) {
    const moneyFormatInput =
      this.form.querySelector('[data-money-format]') ||
      document.querySelector('[data-money-format]');
    const moneyFormat = moneyFormatInput?.value || '${{amount}}';

    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }

    // Fallback
    return (cents / 100).toFixed(2);
  }

  updateDisplayedPrice() {
    if (!this.priceEl) return;

    const priceCents = Number(this.currentVariant?.price || this.variants[0]?.price || 0);
    const qty = this.sanitizeQty(this.qtyInput?.value || 1);

    this.priceEl.textContent = this.formatMoney(priceCents * qty);
  }
}

/* Boot */
document.addEventListener('DOMContentLoaded', () => {
  new ExtMainProduct();
});